import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  getAnnouncementById,
} from '../services/announcementsService';
import Notification from '../components/Common/Notification';

export const AnnouncementContext = createContext();

export const AnnouncementProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllAnnouncements = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAnnouncements(authHeaders);
      setAnnouncements(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching announcements.';
      showNotification(errorMessage, true);
    }
  };

  const fetchAnnouncementById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAnnouncementById(id, authHeaders);
      setSelectedAnnouncement(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching announcement with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addAnnouncement = async (announcementData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newAnnouncement = await createAnnouncement(announcementData, authHeaders);
      setAnnouncements(prevAnnouncements => [...prevAnnouncements, newAnnouncement]);
      showNotification('Announcement added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding announcement.';
      showNotification(errorMessage, true);
    }
  };

  const editAnnouncement = async (id, announcementData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedAnnouncement = await updateAnnouncement(id, announcementData, authHeaders);
      setAnnouncements(prevAnnouncements =>
        prevAnnouncements.map(announcement => (announcement.id === id ? updatedAnnouncement : announcement))
      );
      showNotification('Announcement updated successfully!', false);
      return updatedAnnouncement;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating announcement.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeAnnouncement = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteAnnouncement(id, authHeaders);
      setAnnouncements(prevAnnouncements => prevAnnouncements.filter(announcement => announcement.id !== id));
      showNotification('Announcement removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing announcement.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllAnnouncements();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <AnnouncementContext.Provider
      value={{
        announcements,
        selectedAnnouncement,
        fetchAllAnnouncements,
        fetchAnnouncementById,
        addAnnouncement,
        editAnnouncement,
        removeAnnouncement,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncement = () => useContext(AnnouncementContext);
